import type { IncomeOverview } from '@sea-snag/shared';
import { Button, Input } from '@sea-snag/ui';
import { Lock, Pencil } from 'lucide-react';
import { useState } from 'react';

import { useSetSeasonTaxRate } from '../../hooks/use-income';
import { formatCents } from '../../lib/income';

const SAVED = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** €1,130 split at a rate, for the preview while the rate is edited. */
function preview(ratePct: number, currency: string): string {
  const gross = 113000;
  const net = Math.round((gross * 10_000) / (10_000 + Math.round(ratePct * 100)));
  return `${formatCents(gross, currency, 2)} gross → ${formatCents(gross - net, currency, 2)} tax, ${formatCents(net, currency, 2)} net`;
}

/**
 * The season's charter tax rate (#217), locked once saved. Edit opens it, with
 * a live preview; Save locks it again and Cancel keeps the rate it had. Only
 * this season changes.
 */
export function IncomeTaxSetting({
  orgPid,
  year,
  tax,
  currency,
  canEdit,
}: {
  orgPid: string;
  year: number;
  tax: IncomeOverview['tax'];
  currency: string;
  canEdit: boolean;
}) {
  const save = useSetSeasonTaxRate(orgPid);
  const [draft, setDraft] = useState<string | null>(null);
  const value = Number(draft);
  const valid = draft !== null && draft.trim() !== '' && value >= 0 && value <= 100;

  if (draft === null) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Charter tax for {year}</span>
        <span className="font-medium tabular-nums">{tax.ratePct}%</span>
        {tax.saved ? (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="size-3" aria-hidden />
            saved {tax.savedAt ? SAVED.format(new Date(tax.savedAt)) : ''}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">default, not saved for {year}</span>
        )}
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setDraft(String(tax.ratePct))}
          >
            <Pencil className="mr-1 size-3" aria-hidden />
            {tax.saved ? 'Edit' : 'Set'}
          </Button>
        )}
        <span className="text-xs text-muted-foreground">
          Included in the gross, taken out before the owner and company split.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label htmlFor="income-tax-rate" className="text-muted-foreground">
        Charter tax for {year}
      </label>
      <Input
        id="income-tax-rate"
        type="number"
        min={0}
        max={100}
        step="0.01"
        className="h-7 w-20 text-right text-xs"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <span className="text-muted-foreground">%</span>
      <Button
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={!valid || save.isPending}
        onClick={() =>
          save.mutate({ year, taxRatePct: value }, { onSuccess: () => setDraft(null) })
        }
      >
        Save and lock
      </Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setDraft(null)}>
        Cancel
      </Button>
      {valid && (
        <span className="text-xs text-muted-foreground tabular-nums">
          {preview(value, currency)}
        </span>
      )}
    </div>
  );
}
